import React, { useState } from 'react';
import { Building2, Mail, Phone, MapPin, User, Edit2, Save, X } from 'lucide-react';
import Breadcrumbs from './Breadcrumbs';

function BusinessProfile() {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    companyName: 'ООО "Компания"',
    contactName: 'Иванов Иван Иванович',
    email: 'company@example.com',
    phone: '+7 (999) 123-45-67',
    address: 'г. Москва, ул. Примерная, д. 1, офис 123',
    inn: '1234567890',
    kpp: '123456789',
    ogrn: '1234567890123'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically save the data to your backend
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset form data to original values (would typically fetch from backend)
    setFormData({
      companyName: 'ООО "Компания"',
      contactName: 'Иванов Иван Иванович',
      email: 'company@example.com',
      phone: '+7 (999) 123-45-67',
      address: 'г. Москва, ул. Примерная, д. 1, офис 123',
      inn: '1234567890',
      kpp: '123456789',
      ogrn: '1234567890123'
    });
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="mb-8">
          <Breadcrumbs />
        </div>

        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Профиль компании</h1>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0171E3] hover:bg-[#0156aa] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0171E3]"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Редактировать
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0171E3]"
              >
                <X className="w-4 h-4 mr-2" />
                Отмена
              </button>
              <button
                onClick={handleSubmit}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0171E3] hover:bg-[#0156aa] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0171E3]"
              >
                <Save className="w-4 h-4 mr-2" />
                Сохранить
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Information */}
                <div className="md:col-span-2">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-[#0171E3]" />
                    Информация о компании
                  </h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Название компании
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.companyName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Контактное лицо
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.contactName}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="md:col-span-2">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-[#0171E3]" />
                    Контактная информация
                  </h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <Mail className="w-4 h-4 mr-1 text-gray-500" />
                          Email
                        </label>
                        {isEditing ? (
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.email}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <Phone className="w-4 h-4 mr-1 text-gray-500" />
                          Телефон
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.phone}</p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                          Адрес
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.address}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legal Information */}
                <div className="md:col-span-2">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-[#0171E3]" />
                    Юридическая информация
                  </h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ИНН
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="inn"
                            value={formData.inn}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.inn}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          КПП
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="kpp"
                            value={formData.kpp}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.kpp}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ОГРН
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="ogrn"
                            value={formData.ogrn}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0171E3] focus:border-[#0171E3]"
                          />
                        ) : (
                          <p className="text-gray-900">{formData.ogrn}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessProfile;